import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  disabled,
  FieldTree,
  form,
  FormField,
  min,
  readonly,
  required,
} from '@angular/forms/signals';
import { ButtonModule } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import {
  QuoteRevisionItem,
  QuoteRevisionItemWrite,
} from '../../../../models/quote-revision-item.model';
import {
  getQuantityTypeOptions,
  INHERIT_MATERIAL_ID,
  QuantityMode,
  QuantityType,
  quantityToTime,
  timeToQuantity,
} from '../../../../models/quote-item-data.model';
import { QuoteItemIndexPipe } from '../../../../pipes/quote-item-index.pipe';
import { MaterialSelectComponent } from '../../../shared/material-select/material-select.component';
import { isPermissionError, parseApiError } from '../../../quotes-api-error';
import { PricingModelService } from '../../../services/pricing-model.service';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';
import { PriceSettingComponent } from '../../../shared/price-setting/price-setting.component';
import { TaxCodeSelectComponent } from '../../../tax-code-select/tax-code-select.component';

interface QuoteItemEditFormModel {
  name: string;
  quantity: number;
  quantityHours: number;
  quantityMins: number;
  quantitySecs: number;
  quantityType: QuantityType;
  quantityMode: QuantityMode;
  pricingModel: number | null;
  outputQuantity: boolean;
  material: number | null;
  unitCost: number;
  unitMarkup: number;
  unitMarkupPercent: number;
  taxCodePercentage: number | null;
  unitPrice: number;
  cadInheritedFromOverride: string;
  batchSize: number | null;
}

@Component({
  selector: 'app-quote-revision-item-details-panel',
  imports: [
    CommonModule,
    CurrencyPipe,
    FormsModule,
    InputNumberModule,
    QuoteItemIndexPipe,
    InputTextModule,
    ButtonModule,
    Message,
    FloatLabelModule,
    Divider,
    Tooltip,
    PriceSettingComponent,
    Select,
    MaterialSelectComponent,
    TaxCodeSelectComponent,
    FormField,
  ],
  templateUrl: './item-details.component.html',
})
export class QuoteRevisionItemDetailsPanelComponent implements OnChanges {
  @Input() item!: QuoteRevisionItem;
  @Input({ required: true }) currencyCode!: string;
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Output() relinkCadRequested = new EventEmitter<void>();
  @Output() itemSaved = new EventEmitter<void>();

  private revisionItemService = inject(QuoteRevisionItemService);
  pricingModelService = inject(PricingModelService);
  readonly inheritMaterialId = INHERIT_MATERIAL_ID;

  formModel = signal<QuoteItemEditFormModel>({
    name: '',
    quantity: 0,
    quantityHours: 0,
    quantityMins: 0,
    quantitySecs: 0,
    quantityType: QuantityType.UNIT,
    quantityMode: QuantityMode.SCALES,
    pricingModel: null,
    outputQuantity: false,
    material: null,
    unitCost: 0,
    unitMarkup: 0,
    unitMarkupPercent: 0,
    taxCodePercentage: null,
    unitPrice: 0,
    cadInheritedFromOverride: 'default',
    batchSize: null,
  });

  isSaving = signal(false);
  isFormInitialized = signal(false);

  editForm = form(this.formModel, (p) => {
    required(p.name);
    required(p.quantity);
    required(p.quantityType);
    required(p.quantityMode);
    required(p.unitCost);
    required(p.unitMarkup);
    required(p.unitMarkupPercent);
    min(p.taxCodePercentage, 0);
    required(p.unitPrice);

    readonly(p.quantity, () => this.selectedPricingModelHasOutputQuantity());
    readonly(p.quantityHours, () =>
      this.selectedPricingModelHasOutputQuantity(),
    );
    readonly(p.quantityMins, () =>
      this.selectedPricingModelHasOutputQuantity(),
    );
    readonly(p.quantitySecs, () =>
      this.selectedPricingModelHasOutputQuantity(),
    );
    disabled(p.quantityType, () =>
      this.selectedPricingModelHasOutputQuantity(),
    );
    readonly(p.unitCost, () => this.selectedPricingModelHasOutputUnitCost());
  });

  cadInheritOptions: { id: string; name: string }[] = [];
  saveError: string | null = null;

  selectedPricingModelHasOutputQuantity = computed(() => {
    const pricingModelId = this.formModel().pricingModel;
    if (!pricingModelId) {
      return false;
    }
    const pricingModel =
      this.pricingModelService.pricingModelsById()[pricingModelId];
    return pricingModel?.output_quantity ?? false;
  });

  selectedPricingModelHasOutputUnitCost = computed(() => {
    const pricingModelId = this.formModel().pricingModel;
    if (!pricingModelId) {
      return false;
    }
    const pricingModel =
      this.pricingModelService.pricingModelsById()[pricingModelId];
    return pricingModel?.output_unit_cost ?? false;
  });

  showAppPriceSetting = computed(
    () => !this.selectedPricingModelHasOutputUnitCost(),
  );

  hasBatchSize = computed(() => {
    const batchSize = this.formModel().batchSize;
    return batchSize != null && batchSize > 0;
  });

  unitTotalWithTax = computed(() => {
    const unitPrice = this.editForm.unitPrice().value() ?? 0;
    const taxPercentage = this.editForm.taxCodePercentage().value() ?? 0;
    return unitPrice * (1 + taxPercentage / 100);
  });

  constructor() {
    effect(() => {
      const quantityType = this.editForm.quantityType().value();
      if (quantityType === QuantityType.HOURLY) {
        let hours = this.editForm.quantityHours().value();
        let mins = this.editForm.quantityMins().value();
        let secs = this.editForm.quantitySecs().value();
        if (secs >= 60 || mins >= 60) {
          mins += Math.floor(secs / 60);
          secs %= 60;
          hours += Math.floor(mins / 60);
          mins %= 60;
        }
        if (secs < 0 || mins < 0) {
          if (secs < 0) {
            mins -= 1;
            secs += 60;
          }
          if (mins < 0) {
            hours -= 1;
            mins += 60;
          }
        }
        if (hours < 0) {
          hours = 0;
          mins = 0;
          secs = 0;
        }
        const anyTimeFieldDirty =
          this.editForm.quantityHours().dirty() ||
          this.editForm.quantityMins().dirty() ||
          this.editForm.quantitySecs().dirty();
        this.setValueIfChanged(this.editForm.quantityHours, hours);
        this.setValueIfChanged(this.editForm.quantityMins, mins);
        this.setValueIfChanged(this.editForm.quantitySecs, secs);
        const totalQuantity = timeToQuantity(hours, mins, secs);
        this.setValueIfChanged(this.editForm.quantity, totalQuantity);
        if (anyTimeFieldDirty) {
          this.editForm.quantity().markAsDirty();
        }
      } else {
        const quantity = this.editForm.quantity().value();
        const [newHours, newMins, newSecs] = quantityToTime(quantity);
        this.setValueIfChanged(this.editForm.quantityHours, newHours);
        this.setValueIfChanged(this.editForm.quantityMins, newMins);
        this.setValueIfChanged(this.editForm.quantitySecs, newSecs);
      }
    });
  }

  quantityModeOptions = [
    { label: 'Scales', value: QuantityMode.SCALES },
    { label: 'Fixed', value: QuantityMode.FIXED },
  ];

  quantityTypeOptions = computed(() => {
    const plural = this.formModel().quantity !== 1;
    return getQuantityTypeOptions(plural);
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item) {
      this.isFormInitialized.set(false);
      this.buildCadInheritOptions();
      this.setForm();
      this.isFormInitialized.set(true);
    }
  }

  buildCadInheritOptions(): void {
    this.cadInheritOptions = [
      { id: 'default', name: 'Default' },
      ...(this.item.parent_chain ?? []).map((parent) => ({
        id: parent.persistent_id,
        name: parent.name,
      })),
    ];
  }

  setForm(): void {
    const unitCost = Number.parseFloat(this.item.unit_cost);
    const unitMarkup = Number.parseFloat(this.item.unit_markup);
    const unitPrice = Number.parseFloat(this.item.unit_price);
    const unitMarkupPercent = Number.parseFloat(
      this.item.markup_percentage || '0',
    );
    const materialValue = this.item.inherit_material
      ? INHERIT_MATERIAL_ID
      : (this.item.material_id ?? null);
    const [quantityHours, quantityMins, quantitySecs] = quantityToTime(
      this.item.quantity,
    );

    this.formModel.set({
      name: this.item.name,
      quantity: this.item.quantity,
      quantityHours,
      quantityMins,
      quantitySecs,
      quantityType: this.item.quantity_type,
      quantityMode: this.item.quantity_mode,
      unitCost,
      unitMarkup,
      unitMarkupPercent,
      unitPrice,
      taxCodePercentage:
        this.item.tax_percentage == null
          ? null
          : Number(this.item.tax_percentage),
      pricingModel: this.item.pricing_model_id ?? null,
      material: materialValue,
      outputQuantity: false,
      cadInheritedFromOverride:
        this.item.cad_inherited_from_override_persistent_id ?? 'default',
      batchSize: this.item.batch_size ?? null,
    });

    this.editForm().reset();
    this.saveError = null;
  }

  cancelChanges(): void {
    this.setForm();
  }

  async saveChanges(): Promise<void> {
    if (!this.editForm().valid()) {
      return;
    }

    this.isSaving.set(true);
    this.saveError = null;
    const startTime = Date.now();

    try {
      const patch: Omit<QuoteRevisionItemWrite, 'persistent_id'> = {};

      if (this.editForm.name().dirty()) {
        patch.name = this.editForm.name().value();
      }
      if (
        this.editForm.quantity().dirty() &&
        !this.selectedPricingModelHasOutputQuantity()
      ) {
        patch.quantity = this.editForm.quantity().value();
      }
      if (this.editForm.quantityType().dirty()) {
        patch.quantity_type = this.editForm.quantityType().value();
      }
      if (this.editForm.quantityMode().dirty()) {
        patch.quantity_mode = this.editForm.quantityMode().value();
      }

      const unitCostStr =
        this.editForm.unitCost().value()?.toFixed(2) ?? '0.00';
      if (
        this.selectedPricingModelHasOutputQuantity() ||
        this.item.unit_cost !== unitCostStr
      ) {
        patch.unit_cost = unitCostStr;
      }

      const unitMarkupStr =
        this.editForm.unitMarkup().value()?.toFixed(2) ?? '0.00';
      if (this.item.unit_markup !== unitMarkupStr) {
        patch.unit_markup = unitMarkupStr;
      }

      const unitMarkupPercent =
        this.editForm.unitMarkupPercent().value()?.toFixed(2) ?? '0.00';
      const originalMarkupPercent = this.item.markup_percentage ?? '0.00';
      if (originalMarkupPercent !== unitMarkupPercent) {
        patch.markup_percentage = unitMarkupPercent;
      }

      if (this.editForm.taxCodePercentage().dirty()) {
        patch.tax_percentage =
          this.editForm.taxCodePercentage().value()?.toString() ?? null;
      }

      if (this.editForm.pricingModel().dirty()) {
        patch.pricing_model_id = this.editForm.pricingModel().value();
      }

      if (this.editForm.material().dirty()) {
        const materialValue = this.editForm.material().value();
        if (materialValue === INHERIT_MATERIAL_ID) {
          patch.material_id = null;
          patch.inherit_material = true;
        } else if (materialValue === null) {
          patch.material_id = null;
          patch.inherit_material = false;
        } else {
          patch.material_id = materialValue;
          patch.inherit_material = false;
        }
      }

      if (this.editForm.cadInheritedFromOverride().dirty()) {
        const overrideValue = this.editForm.cadInheritedFromOverride().value();
        patch.cad_inherited_from_override_persistent_id =
          overrideValue === 'default' ? null : overrideValue;
      }

      const batchSize = this.editForm.batchSize().value();
      const hasBatchSize = batchSize != null && batchSize > 0;
      if (this.editForm.batchSize().dirty()) {
        patch.batch_size = hasBatchSize ? batchSize : null;
      }

      const savePromise = this.revisionItemService.updateItem(
        this.item.persistent_id,
        patch,
      );
      const minDelayPromise = new Promise<void>((resolve) => {
        const elapsed = Date.now() - startTime;
        setTimeout(resolve, Math.max(0, 300 - elapsed));
      });

      await Promise.all([savePromise, minDelayPromise]);
      this.setForm();
      this.isSaving.set(false);
      this.itemSaved.emit();
    } catch (error) {
      if (!isPermissionError(error)) {
        this.saveError = parseApiError(
          error,
          'An error occurred while saving the item.',
        );
        console.error('Save error:', error);
      }
      this.isSaving.set(false);
    }
  }

  setControlValue(
    field: FieldTree<unknown, string | number>,
    value: unknown,
  ): void {
    field().controlValue.set(value as never);
  }

  private setValueIfChanged(
    field: FieldTree<unknown, string | number>,
    value: unknown,
  ): void {
    if (field().value() === value) {
      return;
    }
    field().value.set(value as never);
  }

  markTouched(field: FieldTree<unknown, string | number>): void {
    field().markAsTouched();
  }

  resetName(): void {
    this.editForm.name().reset(this.item.name);
  }

  resetQuantity(): void {
    this.editForm.quantity().reset(this.item.quantity);
    this.editForm.quantityType().reset(this.item.quantity_type);
    this.editForm.quantityMode().reset(this.item.quantity_mode);
  }

  resetQuantityMode(): void {
    this.editForm.quantityMode().reset(this.item.quantity_mode);
  }

  resetQuantityType(): void {
    this.editForm.quantityType().reset(this.item.quantity_type);
  }

  resetQuantityValue(): void {
    this.editForm.quantity().reset(this.item.quantity);
    const [quantityHours, quantityMins, quantitySecs] = quantityToTime(
      this.item.quantity,
    );
    this.editForm.quantityHours().reset(quantityHours);
    this.editForm.quantityMins().reset(quantityMins);
    this.editForm.quantitySecs().reset(quantitySecs);
  }

  resetPricingModel(): void {
    this.editForm.pricingModel().reset(this.item.pricing_model_id ?? null);
  }

  resetMaterial(): void {
    const materialValue = this.item.inherit_material
      ? INHERIT_MATERIAL_ID
      : (this.item.material_id ?? null);
    this.editForm.material().reset(materialValue);
  }

  resetCadInheritedFromOverride(): void {
    this.editForm
      .cadInheritedFromOverride()
      .reset(this.item.cad_inherited_from_override_persistent_id ?? 'default');
  }

  resetBatchSize(): void {
    this.editForm.batchSize().reset(this.item.batch_size ?? null);
  }

  resetTaxCodePercentage(): void {
    this.editForm
      .taxCodePercentage()
      .reset(
        this.item.tax_percentage == null
          ? null
          : Number(this.item.tax_percentage),
      );
  }

  resetUnitCost(): void {
    this.editForm.unitCost().reset(Number(this.item.unit_cost) || 0);
  }

  getInputWidth(
    value: number | null | undefined,
    minWidth: number = 1,
  ): string {
    const numValue = value ?? 0;
    const formatted = Number(numValue).toString();
    return `calc(${Math.max(minWidth, formatted.length)}ch + 2em)`;
  }

  readonly QuantityMode = QuantityMode;
  readonly QuantityType = QuantityType;

  get childCount(): number {
    return this.item.children?.length ?? 0;
  }

  get sheetSizeLabel(): string | null {
    const s = this.item.material_sheet_sizes;
    if (!s) {
      return null;
    }
    return `${s.x} × ${s.y} × ${s.z} mm`;
  }

  onRelinkCadRequested(): void {
    this.relinkCadRequested.emit();
  }
}
